// QueryClient de TanStack Query (svelte-query v6) + persister sobre IndexedDB.
//
// Es el cache reactivo de lectura de la app offline-first. El persister guarda
// el cache en IndexedDB (via Dexie) para que sobreviva a cerrar/recargar la app;
// se restaura al boot con <PersistQueryClientProvider> en el layout.
//
// IMPORTANTE (prerender=true + ssr=false): instanciar Dexie abre IndexedDB, que
// no existe en Node. Por eso el persister real solo se crea en browser; en
// build/prerender se usa un noop. El import de Dexie es seguro (no abre nada);
// lo que se protege es el `new Dexie(...)`.
import { QueryClient } from "@tanstack/svelte-query";
import { persistQueryClientRestore } from "@tanstack/query-persist-client-core";
import { browser } from "$app/environment";
import Dexie from "dexie";

// 7 dias: tanto el cache en memoria (gcTime) como el persistido (maxAge).
export const PERSIST_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 min: balance frescura / carga al backend
            gcTime: PERSIST_MAX_AGE, // sobrevive 7 dias sin uso
            retry: 1,
            // Clave del offline-first: sirve el cache antes de probar la red, y
            // si la red falla sigue con el cache en vez de quedar en error.
            networkMode: "offlineFirst",
            refetchOnWindowFocus: true,
        },
    },
});

const CACHE_KEY = "tanstack-query";

function createNoopPersister() {
    return {
        persistClient: async () => {},
        restoreClient: async () => undefined,
        removeClient: async () => {},
    };
}

function createDexiePersister() {
    // Base dedicada SOLO para el persister del cache (no es la BD de la app).
    const db = new Dexie("cf_query_cache");
    db.version(1).stores({ cache: "key, ts" });
    return {
        persistClient: async (client) => {
            await db
                .table("cache")
                .put({ key: CACHE_KEY, value: JSON.stringify(client), ts: Date.now() });
        },
        restoreClient: async () => {
            const row = await db.table("cache").get(CACHE_KEY);
            return row ? JSON.parse(row.value) : undefined;
        },
        removeClient: async () => {
            await db.table("cache").delete(CACHE_KEY);
        },
    };
}

export const persister = browser
    ? createDexiePersister()
    : createNoopPersister();

export const cacheRestored = browser
    ? persistQueryClientRestore({ queryClient, persister, maxAge: PERSIST_MAX_AGE }).catch(() => {})
    : Promise.resolve();
